/**
 * useReportAggregation — фронтенд-хук для AGGREGATE pattern управлінських
 * звітів (UX γ Y-deep P, 2026-05-22).
 *
 * Призначення: звіти типу P&L, Profitability, Cash Flow Report — НЕ
 * використовують dropdown «оберіть org». Замість цього вони агрегують
 * дані по ВСIХ orgs де у user'а є потрібний permission. Hook:
 *  1. Через usePermissionsByOrg(permission) отримує accessibleOrgs
 *  2. Викликає backend з organization_ids=<list> усіх accessible
 *  3. Повертає aggregated data + breakdown_by_org + warnings
 *
 * НЕ використовувати для регламентних звітів (Trial Balance, статутна
 * звітність) — там SINGLE-ORG DROPDOWN pattern (usePermissionsByOrg
 * + dropdown). Деталі у CLAUDE.md «Reports UX patterns».
 *
 * Backend контракт (для P&L commit <next>):
 *   GET <endpoint>?organization_ids=<UUID>&organization_ids=<UUID>&...
 *   Response:
 *     {
 *       lines: T[], totals: ...,                  // aggregated
 *       breakdown_by_org: [{organization_id, organization_name,
 *                           currency, lines, totals}, ...],
 *       currencies: ['UAH', 'USD'],
 *       warnings: ['Юрособи у звіті використовують...']
 *     }
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { usePermissionsByOrg } from './usePermissionsByOrg';
import type { Organization } from '@/api/catalogs';

export interface ReportBreakdown<T> {
  organization_id: string;
  organization_code: string;
  organization_name: string;
  currency: string;
  data: T;
}

export interface ReportAggregationResult<T> {
  /** Aggregated data (loaded). */
  data: T | null;
  /** Per-org breakdown (loaded). Empty array якщо лише 1 org. */
  breakdownByOrg: ReportBreakdown<T>[];
  /** Currency-mismatch warnings + інші серверні попередження. */
  warnings: string[];
  /** Currencies зведені з backend (для UI-логіки). */
  currencies: string[];
  /** Org що відфільтрувались через permission (frontend усе ж знає). */
  accessibleOrgs: Organization[];
  /** Кількість доступних org у tenant'і (для UI хінта). */
  totalAccessibleOrgs: number;
  /** True доки тягнуться /auth/me, /organizations або report. */
  loading: boolean;
  /** True якщо запит впав. */
  error: unknown;
}

/**
 * @param reportEndpoint URL без query, наприклад '/projects/reports/pnl'
 * @param permission код permission'а потрібного для звіту
 * @param extraParams статичні query-параметри для звіту (period_start, тощо)
 */
export function useReportAggregation<T = unknown>(
  reportEndpoint: string,
  permission: string,
  extraParams: Record<string, string | number | undefined> = {},
): ReportAggregationResult<T> {
  const {
    availableOrgs,
    loading: permsLoading,
    totalAccessibleOrgs,
  } = usePermissionsByOrg(permission);

  const orgIds = availableOrgs.map((o) => o.id);
  // Stable cache key — sorted щоб ['a','b'] == ['b','a']
  const orgIdsKey = [...orgIds].sort().join(',');

  // Створюємо stable serialization extraParams для useQuery key
  const extraEntries = Object.entries(extraParams)
    .filter(([, v]) => v !== undefined && v !== '')
    .sort(([a], [b]) => a.localeCompare(b));
  const extraKey = JSON.stringify(extraEntries);

  const reportQuery = useQuery({
    queryKey: ['report-aggregation', reportEndpoint, orgIdsKey, extraKey],
    queryFn: async () => {
      const params = new URLSearchParams();
      for (const id of orgIds) params.append('organization_ids', id);
      for (const [k, v] of extraEntries) params.append(k, String(v));
      const resp = await api.get<{
        // backend response shape (P&L Y-deep)
        lines: unknown;
        totals: unknown;
        breakdown_by_org?: Array<{
          organization_id: string;
          organization_code: string;
          organization_name: string;
          currency: string;
          lines: unknown;
          totals: unknown;
        }>;
        currencies?: string[];
        warnings?: string[];
      }>(`${reportEndpoint}?${params.toString()}`);
      return resp.data;
    },
    enabled: !permsLoading && orgIds.length > 0,
  });

  const raw = reportQuery.data;
  const breakdownByOrg: ReportBreakdown<T>[] = (raw?.breakdown_by_org ?? []).map(
    (b) => ({
      organization_id: b.organization_id,
      organization_code: b.organization_code,
      organization_name: b.organization_name,
      currency: b.currency,
      // Кожен org-breakdown — це той самий "shape" що top-level T
      // (lines + totals). Прокидаємо як data.
      data: { lines: b.lines, totals: b.totals } as unknown as T,
    }),
  );

  return {
    // Top-level T = { lines, totals, ... } — той самий тип що повертає
    // backend для single. Cast необхідний бо useQuery generic не знає shape.
    data: (raw ?? null) as unknown as T | null,
    breakdownByOrg,
    warnings: raw?.warnings ?? [],
    currencies: raw?.currencies ?? [],
    accessibleOrgs: availableOrgs,
    totalAccessibleOrgs,
    loading: permsLoading || reportQuery.isLoading,
    error: reportQuery.error,
  };
}
